import type { FC } from 'react';
import { useState } from 'react';

import { Card } from '@interstate/components/Card';
import { SelectInput } from '@interstate/components/SelectInput';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';

import type { Dealership } from '../../types';
import { SelectInputContainer } from './DealershipSelect.style';
import { OpenDealCardHeader } from '../openDealCardHeader/OpenDealCardHeader';
import { OpenDealCardInfo } from '../Activity.style';

export type DealershipSelectProps = {
    dealers: Array<Dealership>;
    dealerId?: number;
    onDealershipSelect: (dealerId: number) => void;
};

export const DealershipSelect: FC<DealershipSelectProps> = ({ dealers, dealerId, onDealershipSelect }) => {
    const theme = useInterstateTheme();
    const [editMode, setEditMode] = useState(() => false);
    const isAlreadyStarted = dealerId !== undefined;
    const selectedDealer = dealerId ? dealers.find((dealer) => dealer.dealerId === dealerId) : undefined;
    const sortedDealers = dealers.sort((dealerA, dealerB) => dealerA.name.localeCompare(dealerB.name));

    return (
        <Card
            sx={{ backgroundColor: isAlreadyStarted ? theme.tokens.BaseColorGreen50 : theme.tokens.BaseColorGray50 }}
            data-testid="dealership-select"
            header={
                <OpenDealCardHeader
                    title={isAlreadyStarted ? 'Sales Experience' : 'Sales Experience (required)'}
                    showIcon
                    isComplete={isAlreadyStarted}
                    action={editMode ? 'Close' : isAlreadyStarted ? 'Edit' : 'Start'}
                    onActionClick={() => setEditMode(!editMode)}
                />
            }
            content={
                editMode ? (
                    <SelectInputContainer hasValue={!!dealerId}>
                        <SelectInput
                            data-testid="dealership-select-input"
                            onChange={(event) => {
                                onDealershipSelect(Number(event.target.value));
                                setEditMode(!editMode);
                            }}
                            name={'DealershipSelectInput'}
                            options={sortedDealers.map((dealer) => ({ value: `${dealer.dealerId}`, label: dealer.name }))}
                            value={`${dealerId}`}
                            required={false}
                            displayDeselectOption={false}
                            placeholder="Select Sales Experience"
                        ></SelectInput>
                    </SelectInputContainer>
                ) : selectedDealer ? (
                    <OpenDealCardInfo data-testid="dealership-select-dealer-name">{selectedDealer.name}</OpenDealCardInfo>
                ) : null
            }
        ></Card>
    );
};
